import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

// Trailing zero cents, e.g. the `.00` of `$29.00` or the `,00` of `29,00 €`.
const ZERO_CENTS = /([.,])00(?=\D*$)/;

/**
 * Precision, thousands separator and decimal separator for each money format placeholder.
 *
 * @type {Record<string, [number, string, string]>}
 */
const MONEY_RULES = {
  amount: [2, ',', '.'],
  amount_no_decimals: [0, ',', '.'],
  amount_with_comma_separator: [2, '.', ','],
  amount_no_decimals_with_comma_separator: [0, '.', ','],
  amount_with_apostrophe_separator: [2, "'", '.'],
  amount_with_space_separator: [2, ' ', ','],
  amount_with_period_and_space_separator: [2, ' ', '.'],
  amount_no_decimals_with_space_separator: [0, ' ', '.'],
};

/**
 * Renders a price in cents using the shop's money format, e.g. `${{amount}}`.
 *
 * @param {number} cents - The price in cents.
 * @param {string} format - The shop's money format string.
 * @returns {string} The formatted price.
 */
function formatMoney(cents, format) {
  const placeholder = format.match(MONEY_PLACEHOLDER)?.[1] ?? 'amount';
  const [precision, thousands, decimal] = MONEY_RULES[placeholder] ?? MONEY_RULES.amount;
  const [whole = '0', fraction] = (cents / 100).toFixed(precision).split('.');
  const grouped = whole.replace(/(\d)(?=(\d\d\d)+$)/g, `$1${thousands}`);

  return format.replace(MONEY_PLACEHOLDER, fraction ? `${grouped}${decimal}${fraction}` : grouped);
}

/**
 * An upsell line item waiting to be added to the cart.
 *
 * @typedef {object} QueuedUpsell
 * @property {number} id - The variant id.
 * @property {number} quantity - The quantity to add.
 * @property {number} price - The variant price in cents.
 */

/**
 * A custom element that manages the product page upsell list.
 *
 * Clicking an upsell doesn't hit the cart. It queues the variant, adds its price to the total shown
 * on the add to cart button, and flips the upsell button to "Remove". The queue is read by
 * `product-form-component` on submit so everything is added to the cart in a single request.
 *
 * @extends Component
 */
class ProductUpsellsComponent extends Component {
  #abortController = new AbortController();

  /**
   * Queued upsells, keyed by variant id and kept in the order they were clicked.
   *
   * @type {Map<string, QueuedUpsell>}
   */
  #queue = new Map();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // The buy buttons are re-rendered on variant change, which wipes the total we injected.
    document.addEventListener(ThemeEvents.variantUpdate, this.#renderTotal, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Re-applies the queued state after the list is re-rendered, e.g. when a combined listing swaps
   * the product out from under us.
   */
  updatedCallback() {
    super.updatedCallback();

    this.#render();
  }

  /**
   * The upsells queued for the next add to cart. Read by `product-form-component` on submit.
   *
   * @returns {{id: number, quantity: number}[]} The queued line items.
   */
  getQueuedItems() {
    return [...this.#queue.values()].map(({ id, quantity }) => ({ id, quantity }));
  }

  /**
   * Adds the clicked upsell to the queue, or removes it if it's already queued.
   *
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  toggle(event) {
    const button = event.target.closest('.upsell-add-btn');
    const item = button?.closest('.upsell-item');

    if (!(button instanceof HTMLButtonElement) || !(item instanceof HTMLElement)) return;

    const { variantId, price } = item.dataset;

    if (!variantId) return;

    if (this.#queue.has(variantId)) {
      this.#queue.delete(variantId);
    } else {
      this.#queue.set(variantId, { id: Number(variantId), quantity: 1, price: Number(price) || 0 });
    }

    this.#render();
  }

  /**
   * Brings the upsell buttons and the add to cart total in line with the queue, dropping any
   * queued variant that is no longer in the list.
   */
  #render() {
    /** @type {NodeListOf<HTMLElement>} */
    const items = this.querySelectorAll('.upsell-item');
    const listed = new Set([...items].map((item) => item.dataset.variantId));

    for (const variantId of this.#queue.keys()) {
      if (!listed.has(variantId)) this.#queue.delete(variantId);
    }

    for (const item of items) {
      const button = item.querySelector('.upsell-add-btn');
      const { variantId } = item.dataset;

      if (button && variantId) this.#renderButton(button, this.#queue.has(variantId));
    }

    this.#renderTotal();
  }

  /**
   * Empties the queue once the items have actually made it into the cart.
   *
   * @param {Event & {detail?: {data?: {didError?: boolean, source?: string, productId?: string}}}} event - The cart update event.
   */
  #onCartUpdate = (event) => {
    const { didError, source, productId } = event.detail?.data ?? {};

    if (didError || source !== 'product-form-component' || productId !== this.dataset.productId) return;
    if (!this.#queue.size) return;

    this.#queue.clear();
    this.#render();
  };

  /**
   * Writes the main product's price plus every queued upsell onto the add to cart button.
   */
  #renderTotal = () => {
    const queuedTotal = [...this.#queue.values()].reduce((total, { price }) => total + price, 0);

    for (const label of this.#priceLabels) {
      const { basePrice } = label.dataset;

      if (basePrice == null || Number.isNaN(Number(basePrice))) continue;

      // The buy buttons render the price without trailing zero cents, so match that.
      label.textContent = formatMoney(Number(basePrice) + queuedTotal, this.#moneyFormat).replace(ZERO_CENTS, '');
    }
  };

  /**
   * The price labels on this product's add to cart buttons. Scoped to the buy buttons block so
   * quick add buttons for other products aren't touched.
   *
   * @returns {HTMLElement[]} The price labels.
   */
  get #priceLabels() {
    const scope = this.closest('.buy-buttons-block') ?? this.closest('.shopify-section');

    return [.../** @type {NodeListOf<HTMLElement>} */ (scope?.querySelectorAll('[data-button-price-atc]') ?? [])];
  }

  /**
   * @returns {string} The shop's money format.
   */
  get #moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  /**
   * Reflects the queued state on an upsell button.
   *
   * @param {Element} button - The upsell button.
   * @param {boolean} queued - Whether the upsell is queued.
   */
  #renderButton(button, queued) {
    if (!(button instanceof HTMLElement)) return;

    const label = queued ? this.dataset.removeLabel : this.dataset.addLabel;
    const text = button.querySelector('.btn-text');

    if (text && label) text.textContent = label;

    if (label && button.dataset.upsellTitle) {
      button.setAttribute('aria-label', `${label} ${button.dataset.upsellTitle}`);
    }

    button.dataset.action = queued ? 'remove' : 'add';
    button.classList.toggle('is-active', queued);
    button.setAttribute('aria-pressed', String(queued));
  }
}

if (!customElements.get('product-upsells-component')) {
  customElements.define('product-upsells-component', ProductUpsellsComponent);
}
